<template>
  <div class="internal-code-selector">
    <div class="selector-wrapper" ref="selectorRef">
      <div
        class="selector-input"
        :class="{ 'open': isOpen, 'missing': !modelValue && props.required }"
        @mousedown.stop="toggleDropdown"
      >
        <div class="selected-value">
          <span v-if="props.modelValue" class="selected-code">
            {{ props.modelValue }}
          </span>
          <!-- Nothing when it is not filled: "IC" read as a value that was
               already chosen. The empty field and its edge say it is open. -->
        </div>
        <LineIcon
          name="chevron-down"
          :size="11"
          :stroke-width="1.8"
          class="dropdown-icon"
          :class="{ 'rotate': isOpen }"
        />
      </div>

      <Teleport to="body">
        <div
          v-if="isOpen"
          class="dropdown-menu"
          :style="dropdownStyle"
        >
          <div class="dropdown-list">
            <div
              v-if="!modelValue"
              class="dropdown-item placeholder"
              @mousedown.stop="selectCode(null)"
            >
              <div class="code-info">None</div>
            </div>
            <div
              v-for="code in uniqueInternalCodes"
              :key="code"
              class="dropdown-item"
              :class="{ 'selected': code === modelValue }"
              @mousedown.stop="selectCode(code)"
            >
              <div class="code-info">
                {{ code }}
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, inject, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: null
  },
  accountCodes: {
    type: Array,
    required: true
  },
  required: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'selected']);

const isOpen = ref(false);
const selectorRef = ref(null);
const dropdownId = ref(null);
const dropdownStyle = ref({});

// Inject active dropdown management
const activeDropdownId = inject('activeDropdownId', ref(null));
const setActiveDropdown = inject('setActiveDropdown', () => {});

// Generate unique ID for this dropdown
onMounted(() => {
  dropdownId.value = Math.random().toString(36).substring(7);
});

// Watch for other dropdowns opening
watch(activeDropdownId, (newActiveId) => {
  if (newActiveId !== dropdownId.value && isOpen.value) {
    isOpen.value = false;
  }
});

// Get unique internal codes from account codes
const uniqueInternalCodes = computed(() => {
  // Always include the standard codes
  const standardCodes = ['S', 'U', 'M', 'P'];
  const allCodes = [...standardCodes];
  
  props.accountCodes.forEach(ac => {
    if (ac.internal_code && ac.internal_code.trim() !== '') {
      // Split by comma in case multiple codes are in one field
      const codes = ac.internal_code.split(',').map(c => c.trim()).filter(c => c !== '');
      allCodes.push(...codes);
    }
  });
  
  // Return unique codes sorted, but with standard codes first
  const uniqueCodes = [...new Set(allCodes)];
  return uniqueCodes.sort((a, b) => {
    const aStandard = standardCodes.indexOf(a);
    const bStandard = standardCodes.indexOf(b);
    
    // If both are standard codes, sort by their index in standardCodes
    if (aStandard !== -1 && bStandard !== -1) {
      return aStandard - bStandard;
    }
    // If only a is standard, it comes first
    if (aStandard !== -1) return -1;
    // If only b is standard, it comes first
    if (bStandard !== -1) return 1;
    // Otherwise, sort alphabetically
    return a.localeCompare(b);
  });
});

const updateDropdownPosition = () => {
  if (!selectorRef.value) return;
  
  const rect = selectorRef.value.getBoundingClientRect();
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `80px`
  };
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setActiveDropdown(dropdownId.value);
    updateDropdownPosition();
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  if (activeDropdownId.value === dropdownId.value) {
    setActiveDropdown(null);
  }
};

const selectCode = (code) => {
  emit('update:modelValue', code);
  emit('selected', code);
  closeDropdown();
};

// Handle clicks outside
const handleClickOutside = (event) => {
  if (selectorRef.value && !selectorRef.value.contains(event.target)) {
    closeDropdown();
  }
};

// Update position on scroll
const handleScroll = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleScroll);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleScroll);
});
</script>

<style scoped>
.internal-code-selector {
  position: relative;
  width: 100%;
}

.selector-wrapper {
  position: relative;
  width: 100%;
}

.selector-input {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 18px 0 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 38px;
  z-index: 10001;
  width: 38px;
}

.selector-input:hover {
  border-color: #d1d5db;
}

.selector-input.missing {
  border-color: #fb923c;
  background-color: #fff7ed;
}

.selector-input.missing:hover {
  border-color: #f97316;
  background-color: #ffedd5;
}

.selector-input.open {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.selected-value {
  flex: 1;
  font-size: 13px;
  color: #1f2937;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.placeholder-text {
  color: #1f2937;
  font-size: 13px;
  font-weight: 400;
}

.dropdown-icon {
  position: absolute;
  right: 5px;
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
  font-size: 8px;
  pointer-events: none;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 10002;
  max-height: 200px;
  overflow: hidden;
}

.dropdown-list {
  max-height: 200px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 6px 10px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f9fafb;
}

.dropdown-item.selected {
  background-color: #eff6ff;
}

.dropdown-item.placeholder {
  color: #9ca3af;
  font-style: italic;
  text-align: center;
  font-size: 12px;
}

.code-info {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
}

.selected-code {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.internal-code-badge-display {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

/* ── At the product's scale ────────────────────────────────────────────
 *
 * The field matched nothing else: 38px tall with its own greys, and when a
 * value was still missing it turned amber — a colour this product uses
 * nowhere. Same hairline field as everywhere, and "still to be filled" is
 * said by the edge, in the warning colour the rest of the app uses. */
/* The box is as narrow as it can be, so its padding has to leave room for the
   value: 38px wide against 38px of padding left the text nothing to occupy and
   a chosen code simply never appeared. */
.selector-input {
  min-height: 32px;
  height: 32px;
  width: 56px;
  padding: 0 16px 0 8px;
  justify-content: flex-start;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  font-size: 13px;
}

.selected-value {
  justify-content: flex-start;
}

.selected-code {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  right: 6px;
}

.selector-input:hover {
  border-color: var(--primary, #0697e4);
}

.selector-input.missing {
  border-color: #f0b429;
  background-color: #fff;
}

.selector-input.missing:hover {
  border-color: #d99a0b;
  background-color: #fff;
}

.selector-input.open {
  border-color: var(--primary, #0697e4);
  box-shadow: none;
}

.selected-value {
  font-size: 13px;
}
</style>
